import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import api from '../../services/api';
import { getUserGroupNationalities } from '../../services/userGroupService';
import AsyncGenericSelect from './AsyncGenericSelect';
import GenericSelect from './GenericSelect';
import TextInput from './TextInput.jsx';
import DateTimePicker from './DateTimePicker';
import { positionAsync } from '../../pages/positions/config';
import {
  CRITERION, OP, allowedOperators, firstOperator, emptyValue,
  ENTITY_TYPES, DATE_TYPES, NUMBER_TYPES, ENUM_TYPES,
  criterionTypeLabel, operatorLabel, numberRange,
} from './criterionMeta';


export default function GroupCriteriaBuilder({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const rows = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const [nationalities, setNationalities] = useState([]);

  // Uyruk listesi kiracıda FİİLEN var olan değerlerden gelir: serbest metin
  // yazdırmak Türkçe i/İ tuzağı ve tipo yüzünden sessizce eşleşmeyen kriter üretirdi.
  useEffect(() => {
    let iptal = false;
    getUserGroupNationalities()
      .then((res) => {
        if (iptal) return;
        const r = res.data?.data;
        setNationalities(Array.isArray(r) ? r : []);
      })
      .catch(() => { /* interceptor */ });
    return () => { iptal = true; };
  }, []);

  // Ekrandaki sıra: örgütsel boyutlar, sonra nitelikler, sonra karşılaştırmalı alanlar.
  const TYPE_ORDER = useMemo(() => [
    CRITERION.DEPARTMENT, CRITERION.LOCATION, CRITERION.SUB_TENANT, CRITERION.TITLE,
    CRITERION.GRADE, CRITERION.JOB_FAMILY,
    CRITERION.POSITION, CRITERION.EMPLOYEE_STATUS, CRITERION.COLLAR_TYPE, CRITERION.EMPLOYMENT_TYPE,
    CRITERION.GENDER, CRITERION.MARITAL_STATUS, CRITERION.NATIONALITY,
    CRITERION.EMPLOYMENT_DATE, CRITERION.SENIORITY_MONTHS,
    CRITERION.NUMBER_OF_CHILDREN, CRITERION.AGE, CRITERION.DATE_OF_BIRTH,
  ], []);

  const typeOptions = useMemo(
    () => TYPE_ORDER.map((tip) => ({ id: tip, name: criterionTypeLabel(tip, t) })),
    [TYPE_ORDER, t]);

  const opLabel = useCallback((op, tip) => operatorLabel(op, t, tip), [t]);

  const enumOptions = useMemo(() => ({
    [CRITERION.COLLAR_TYPE]: [
      { id: 1, name: t('employee.whiteCollar') },
      { id: 2, name: t('employee.blueCollar') },
    ],
    [CRITERION.EMPLOYMENT_TYPE]: [
      { id: 1, name: t('employee.fullTime') },
      { id: 2, name: t('employee.partTime') },
      { id: 3, name: t('employee.contract') },
    ],
    [CRITERION.GENDER]: [
      { id: 1, name: t('employee.male') },
      { id: 2, name: t('employee.female') },
      { id: 3, name: t('employee.preferNotToSay') },
    ],
    [CRITERION.MARITAL_STATUS]: [
      { id: 1, name: t('employee.married') },
      { id: 2, name: t('employee.single') },
      { id: 3, name: t('employee.divorced') },
    ],
  }), [t]);

  const entityAsync = useMemo(() => ({
    [CRITERION.DEPARTMENT]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/departments', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((d) => ({ id: d.id, name: d.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/departments/${id}`);
        return { id, name: res.data?.data?.name || '' };
      },
    },
    [CRITERION.LOCATION]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/locations', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((l) => ({ id: l.id, name: l.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/locations/${id}`);
        return { id, name: res.data?.data?.name || '' };
      },
    },
    [CRITERION.TITLE]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/titles', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((x) => ({ id: x.id, name: x.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/titles/${id}`);
        return { id, name: res.data?.data?.name || '' };
      },
    },
    // Personel statüsü (AKM POC §6.1): ad araması `name[like]`, tek kayıt okuma ucu VAR.
    [CRITERION.EMPLOYEE_STATUS]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/employee-statuses', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((x) => ({ id: x.id, name: x.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/employee-statuses/${id}`);
        return { id, name: res.data?.data?.name || '' };
      },
    },
    // Kademe: etikette SEVİYE de yazar — "En Az Müdür (Seviye 4)" sıralı okunur.
    [CRITERION.GRADE]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 50 } : { pageSize: 50 };
        const res = await api.get('/v1/job-grades', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((x) => ({ id: x.id, name: `${x.name} (${t('jobGrade.levelShort')} ${x.level})` }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/job-grades/${id}`);
        const g = res.data?.data;
        return { id, name: g ? `${g.name} (${t('jobGrade.levelShort')} ${g.level})` : '' };
      },
    },
    [CRITERION.JOB_FAMILY]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 50 } : { pageSize: 50 };
        const res = await api.get('/v1/job-families', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((x) => ({ id: x.id, name: x.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/job-families/${id}`);
        return { id, name: res.data?.data?.name || '' };
      },
    },
    // Pozisyonun GÖRÜNEN ADI YOKTUR: PositionResponse `name` taşımaz ve filtre
    // sözleşmesi `name[like]` tanımaz. Deponun tek pozisyon seçici yardımcısı.
    [CRITERION.POSITION]: positionAsync,
    // Alt şirket ucu /v1/sub-tenants'tır ve TEK kayıt okuma ucu YOKTUR:
    // onGetById listeden bulur (employees/config'teki kanıtlanmış desen).
    [CRITERION.SUB_TENANT]: {
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/sub-tenants', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((x) => ({ id: x.id, name: x.name }));
      },
      onGetById: async (id) => {
        const res = await api.get('/v1/sub-tenants', { params: { pageSize: 100 } });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        const st = items.find((x) => x.id === id);
        return st ? { id: st.id, name: st.name } : { id, name: '' };
      },
    },
  }), [t]);

  const satirGuncelle = useCallback((i, yama) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...yama } : r)));
  }, [rows, onChange]);

  const tipDegistir = useCallback((i, yeniTip) => {
    satirGuncelle(i, {
      criterionType: yeniTip,
      operator: firstOperator(yeniTip),
      ...emptyValue(),
    });
  }, [satirGuncelle]);

  const satirEkle = useCallback(() => {
    onChange([
      ...rows,
      { criterionType: CRITERION.COLLAR_TYPE, operator: OP.EQ, ...emptyValue() },
    ]);
  }, [rows, onChange]);

  const satirSil = useCallback((i) => {
    onChange(rows.filter((_, idx) => idx !== i));
  }, [rows, onChange]);

  const degerAlani = (row, i) => {
    const tip = row.criterionType;

    if (ENTITY_TYPES.includes(tip)) {
      const cfg = entityAsync[tip] || {};
      return (
        <WideField data-testid={`criterion-entity-${i}`}>
          <AsyncGenericSelect
            value={row.targetId ?? null}
            onChange={(val) => satirGuncelle(i, { targetId: val })}
            onSearch={cfg.onSearch}
            onGetById={cfg.onGetById}
            disabled={disabled}
          />
        </WideField>
      );
    }

    if (ENUM_TYPES.includes(tip)) {
      return (
        <WideField data-testid={`criterion-enum-${i}`}>
          <GenericSelect
            options={enumOptions[tip] || []}
            value={row.valueInt ?? null}
            onChange={(val) => satirGuncelle(i, { valueInt: val ?? null })}
            idKey="id"
            labelKey="name"
            disabled={disabled}
          />
        </WideField>
      );
    }

    if (tip === CRITERION.NATIONALITY) {
      return (
        <WideField data-testid={`criterion-nationality-${i}`}>
          <GenericSelect
            options={nationalities.map((n) => ({ id: n, name: n }))}
            value={row.valueText ?? null}
            onChange={(val) => satirGuncelle(i, { valueText: val || null })}
            idKey="id"
            labelKey="name"
            disabled={disabled}
          />
        </WideField>
      );
    }

    if (DATE_TYPES.includes(tip)) {
      // Tarih alanında serbest metin YASAK — DateTimePicker kullanılır.
      return (
        <WideField data-testid={`criterion-date-${i}`}>
          <DateTimePicker
            mode="date"
            value={row.valueDate ?? ''}
            onChange={(val) => satirGuncelle(i, { valueDate: val || null })}
            disabled={disabled}
          />
        </WideField>
      );
    }

    if (NUMBER_TYPES.includes(tip)) {
      return (
        <NarrowField data-testid={`criterion-number-${i}`}>
          <TextInput
            type="number"
            min={numberRange(tip).min}
            max={numberRange(tip).max}
            disabled={disabled}
            value={row.valueInt ?? ''}
            onChange={(e) => satirGuncelle(i, {
              valueInt: e.target.value === '' ? null : Number(e.target.value),
            })}
          />
        </NarrowField>
      );
    }

    return null;
  };

  return (
    <Box>
      <BoxLabel>{t('userGroup.criteria')}</BoxLabel>
      <Section>
        <SectionHead>
          <SectionTitle>{t('userGroup.criteriaRules')}</SectionTitle>
          {!disabled && (
            <AddBtn type="button" data-testid="criterion-add" onClick={satirEkle}>
              <Plus size={13} /> {t('userGroup.addCriterion')}
            </AddBtn>
          )}
        </SectionHead>
        <Hint>{t('userGroup.criteriaHint')}</Hint>

        {rows.length === 0 && <Empty>{t('userGroup.criteriaEmpty')}</Empty>}

        {rows.map((row, i) => {
          const izinliOps = allowedOperators(row.criterionType);
          return (
            <Row key={i}>
              <TypeField data-testid={`criterion-type-${i}`}>
                <GenericSelect
                  label={t('userGroup.criterionType')}
                  options={typeOptions}
                  value={row.criterionType ?? null}
                  onChange={(val) => tipDegistir(i, Number(val))}
                  idKey="id"
                  labelKey="name"
                  disabled={disabled}
                />
              </TypeField>

              {/* Tek operatörlü tiplerde seçici GÖSTERİLMEZ: kullanıcıya seçenek
                  sunmayan bir kutu göstermek gürültüdür. */}
              {izinliOps.length > 1 && (
                <OpField data-testid={`criterion-operator-${i}`}>
                  <GenericSelect
                    label={t('userGroup.criterionOperator')}
                    options={izinliOps.map((op) => ({ id: op, name: opLabel(op, row.criterionType) }))}
                    value={row.operator ?? izinliOps[0]}
                    onChange={(val) => satirGuncelle(i, { operator: Number(val) })}
                    idKey="id"
                    labelKey="name"
                    disabled={disabled}
                  />
                </OpField>
              )}

              <ValueField>
                <FieldLabel>{t('userGroup.criterionValue')}</FieldLabel>
                {degerAlani(row, i)}
              </ValueField>

              {!disabled && (
                <DelBtn
                  type="button"
                  data-testid={`criterion-remove-${i}`}
                  onClick={() => satirSil(i)}
                >
                  <Trash2 size={14} />
                </DelBtn>
              )}
            </Row>
          );
        })}
      </Section>
    </Box>
  );
}

// `overflow: hidden` BİLEREK YOK. Köşe yuvarlaması için konmuştu ama
// GenericSelect'in menüsü `position: absolute` ile çizildiği için bu kutu onu
// SERT KIRPIYORDU: kriter tipi listesi kutunun altına taşınca seçenekler
// görünmüyordu ve kutu kaydırmadığı için kaydırarak da ulaşılamıyordu.
// (GenericFormDrawer'ın gövdesi `overflow-y: auto` ile kaydırıyor; oradaki
// kırpma kaydırılabilir olduğu için sorun değil — sert kırpan katman buydu.)
// Kutunun içinde kenara değen arka planlı çocuk yok, yani kaldırmanın görsel
// yan etkisi de yok.
const Box = styled.div`
  border: 1.5px solid #E0E4E8;
  border-radius: 6px;
`;

const BoxLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 12px 16px 0;
`;

const Section = styled.div`
  padding: 12px 16px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SectionTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 0;
  &:hover { text-decoration: underline; }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 6px;
  /* Dar çekmecede alanlar yatayda taşmak yerine alt satıra iner. */
  flex-wrap: wrap;
`;


const FieldLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #94a3b8;
`;


const TypeField = styled.div`
  width: 200px;
  flex-shrink: 0;
`;

const OpField = styled.div`
  width: 110px;
  flex-shrink: 0;
`;

const ValueField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const WideField = styled.div`
  width: 100%;
  min-width: 140px;
`;

const NarrowField = styled.div`
  width: 110px;
`;

const DelBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: rgba(229, 57, 53, 0.08); color: #E53935; }
`;

const Hint = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 8px;
  line-height: 1.45;
`;

const Empty = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
  padding: 4px 0;
`;
